import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  Field,
  Clipboard,
} from '@chakra-ui/react'
import { ChangeEvent, FormEvent, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { submitContactFormAction } from 'reduxStore/contact/api'
import { resetContactFormState } from 'reduxStore/contact/contactSlice'

const ContactUs = ({ asModal }: { asModal?: boolean }) => {
  const dispatch = useDispatch()
  const { contactFormState } = useSelector((state: any) => state.contact)
  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    email: '',
    monthlyFxVolume: '',
    message: '',
  })

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = (event: any) => {
    event.preventDefault()
    dispatch<any>(submitContactFormAction(formData))
  }

  useEffect(() => {
    return () => {
      dispatch(resetContactFormState())
    }
  }, [dispatch])

  return (
    <Box maxW='1140px' m={asModal ? '0' : '150px auto'}>
      <Flex direction='column' gap={16}>
        {!asModal ? (
          <Stack gap={4} maxW='3xl' m='auto'>
            <Heading size='2xl' color='darkBlue' textAlign='center'>
              Let&rsquo;s Simplify Your FX Operations.
            </Heading>
            <Text fontSize='md' color='gray.600'>
              Have questions or ready to see how Worldesk can transform your
              global payments? We&rsquo;re here to help. Fill out the form below
              and one of our FX specialists will reach out within 24 hours.
            </Text>
          </Stack>
        ) : null}

        <SimpleGrid
          columns={{ base: 1, md: 2 }}
          gap={12}
          alignItems='start'
          display={!asModal ? 'grid' : 'block'}
        >
          <Box as='form' display='grid' gap={6} onSubmit={handleSubmit}>
            {/* name required */}
            <Field.Root required>
              <Field.Label color='darkBlue'>
                Name
                <Field.RequiredIndicator />
              </Field.Label>
              <Input
                name='name'
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Field.Root>
            <Field.Root>
              <Field.Label color='darkBlue'>Company Name</Field.Label>
              <Input
                name='companyName'
                value={formData.companyName}
                onChange={handleChange}
              />
            </Field.Root>
            {/* email required */}
            <Field.Root required>
              <Field.Label color='darkBlue'>
                Email Address
                <Field.RequiredIndicator />
              </Field.Label>
              <Input
                name='email'
                type='email'
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Field.Root>
            <Field.Root>
              <Field.Label color='darkBlue'>Monthly FX Volume</Field.Label>
              <Input
                name='monthlyFxVolume'
                value={formData.monthlyFxVolume}
                onChange={handleChange}
              />
            </Field.Root>
            <Field.Root>
              <Field.Label color='darkBlue'>Message / Comments</Field.Label>
              <Textarea
                name='message'
                rows={5}
                value={formData.message}
                onChange={handleChange}
              />
            </Field.Root>
            <Button
              type='submit'
              variant='ghost'
              h='45px'
              p='0px 15px'
              bgColor='#008CFF'
              fontWeight='bold'
              textTransform='uppercase'
              fontSize='xs'
              color='#fff'
              disabled={contactFormState?.isFetching}
            >
              {contactFormState?.isFetching ? 'Loading...' : 'Submit'}
            </Button>

            {contactFormState?.isFetched && !contactFormState?.isFailed && (
              <Text fontSize='sm' color='green.500'>
                Thanks! We received your request and will reach out soon.
              </Text>
            )}
            {contactFormState?.isFailed && (
              <Text fontSize='sm' color='red.500'>
                {contactFormState?.errorMessage ||
                  'Something went wrong. Please try again.'}
              </Text>
            )}
          </Box>

          {!asModal ? (
            <Stack
              gap={6}
              bgColor='gray.50'
              p={{ base: 6, md: 8 }}
              borderRadius='lg'
            >
              <Heading size='md' color='darkBlue'>
                Prefer direct communication?
              </Heading>
              <Stack gap={2}>
                <Text fontSize='sm' fontWeight='medium'>
                  Email
                </Text>
                <Text fontSize='sm' color='gray.600'></Text>
                <Clipboard.Root value='info@worldesk.com'>
                  <Clipboard.Trigger asChild>
                    <Button
                      as='span'
                      color='blue.fg'
                      textStyle='sm'
                      variant='ghost'
                      bgColor='gray.100'
                    >
                      <Clipboard.Indicator />
                      <Clipboard.ValueText />
                    </Button>
                  </Clipboard.Trigger>
                </Clipboard.Root>
              </Stack>
              <Stack gap={2}>
                <Text fontSize='sm' fontWeight='medium'>
                  Phone
                </Text>
                <Clipboard.Root value='(212) 884-9912'>
                  <Clipboard.Trigger asChild>
                    <Button
                      as='span'
                      color='blue.fg'
                      textStyle='sm'
                      variant='ghost'
                      bgColor='gray.100'
                    >
                      <Clipboard.Indicator />
                      <Clipboard.ValueText />
                    </Button>
                  </Clipboard.Trigger>
                </Clipboard.Root>
              </Stack>
              <Text fontSize='sm' color='gray.700'>
                Your global payments partner is just a conversation away.
              </Text>
            </Stack>
          ) : null}
        </SimpleGrid>
      </Flex>
    </Box>
  )
}

export default ContactUs
